<template>
  <el-card>
    <h3 slot="header">
      <el-button
        v-if="paymentDefault"
        type="primary"
        size="mini"
        @click="changePayment = true"
      >
        Add
      </el-button>
      Payment methods
    </h3>

    <div v-if="paymentDefault && !changePayment" v-loading="loading">
      <el-row :gutter="15">
        <el-col v-if="customer" :span="10">
          <h4 class="billing-title">Billing address :</h4>
          <Details
            :edit="editBillingDetails"
            :address.sync="details.address"
            :name.sync="details.name"
            :email.sync="details.email"
            size="mini"
          />

          <el-button
            size="small"
            :type="editBillingDetails ? 'primary' : 'info'"
            @click.prevent="updateBillingDetails"
          >
            Update information
          </el-button>
        </el-col>

        <el-col :span="14">
          <div
            v-for="method in paymentMethods"
            :key="method.id"
            v-bind:class="{
              [method.type === 'card' ? method.card.brand : 'sepa_debit']: true,
              'payment-info': method.type === 'card',
              'bank-info': method.type === 'sepa_debit'
            }"
          >
            <template v-if="method.type === 'card'">
              &bullet;&bullet;&bullet;&bullet; <strong>{{ method.card.last4 }}</strong>
            </template>
            <template v-if="method.type === 'sepa_debit'">
              <svg focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 22 22"><path class="Icon-fill" d="M9.51 2.29l7.21 4.61a.6.6 0 0 1-.34 1.1H.62a.6.6 0 0 1-.34-1.1l7.21-4.61a1.89 1.89 0 0 1 2.02 0zM1.08 16.47l-.83 2c-.12.28 0 .57.28.57h15.66c.27 0 .41-.29.29-.57l-.84-2A.73.73 0 0 0 15 16H1.73a.74.74 0 0 0-.65.47z" opacity=".4"></path><path class="Icon-fill" d="M2 9h2.5v6H2zm10.5 0H15v6h-2.5zM7.25 9h2.5v6h-2.5z" opacity=".2"></path></svg>
              SEPA Direct Debit &bullet;&bullet;&bullet;&bullet; <strong>{{ method.sepa_debit.last4 }}</strong>
            </template>

            <el-tag v-if="method.id === paymentDefault" size="mini" type="primary">default</el-tag>

            <template v-else>
              <el-button
                v-if="paymentMethods.length > 1 && method.type === 'card' && customer.invoice_settings.default_payment_method !== method.id"
                type="danger"
                size="mini"
                icon="el-icon-delete"
                @click.prevent="remove(method)"
              />
              <el-button
                type="text"
                size="mini"
                @click.prevent="updateDefault(method)"
              >
                Set as default
              </el-button>
            </template>

            <div>
              <small>
                <template v-if="method.type === 'card'">
                  Expires {{ method.card.exp_month.toString().padStart(2, 0) }}/{{ method.card.exp_year }}
                  &bullet;
                </template>
                Added on {{ getAddedDate(method) }}
              </small>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <el-tabs v-else>
      <el-tab-pane label="Card">
        <StripeCard
          shadow="none"
          :show-cancel="!!customer"
          :show-details="!customer"
          @save="$emit('change')"
          @cancel="changePayment = false"
        />
      </el-tab-pane>
      <el-tab-pane label="SEPA Debit">
        <StripeSepa
          shadow="none"
          :show-cancel="!!customer"
          @save="$emit('change')"
          @cancel="changePayment = false"
        />
      </el-tab-pane>
    </el-tabs>
  </el-card>
</template>

<script>
import moment from 'moment-timezone'
import StripeCard from './StripeCard'
import StripeSepa from './StripeSepa'
import Details from './Details'

export default {
  props: {
    customer: Object,
    paymentDefault: String,
    paymentMethods: {
      type: Array,
      default: () => []
    }
  },
  components: {
    StripeCard,
    StripeSepa,
    Details
  },
  data () {
    return {
      loading: false,
      editBillingDetails: false,
      changePayment: false,
      details: {
        name: null,
        email: null,
        address: {}
      }
    }
  },
  created () {
    if (this.customer) {
      this.details = {
        name: this.customer.name,
        email: this.customer.email,
        address: { ...(this.customer.address || {}) }
      }
    }
  },
  methods: {
    updateBillingDetails () {
      if (this.editBillingDetails) {
        this.loading = true
        this.$store.dispatch('updateBillingCustomer', this.details).then((customer) => {
          this.$emit('update:customer', customer)
          this.loading = false
          this.editBillingDetails = false
        }).catch(() => {})
      }
      this.editBillingDetails = true
    },
    updateDefault (method) {
      this.loading = true
      this.$store.dispatch('savePaymentMethod', {
        payment_method: method.id
      }).then((customer) => {
        this.$emit('change', method.id)
        this.loading = false
      }).catch(() => {})
    },
    remove (method) {
      this.$confirm('Remove this payment method ?', 'Warning', {
        confirmButtonText: 'Remove',
        cancelButtonText: 'Cancel',
        confirmButtonClass: 'el-button--danger'
      }).then(() => {
        this.loading = true
        return this.$store.dispatch('removePaymentMethod', method.id).then(() => {
          this.$emit('change', method.id)
          this.loading = false
        })
      }).catch(() => {})
    },
    getAddedDate (method) {
      return moment.unix((method || {}).created).format('DD MMM YYYY')
    }
  },
  watch: {
    customer: {
      handler (value) {
        this.details = {
          name: value.name,
          email: value.email,
          address: { ...(value.address || {}) }
        }
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "../../theme";

  h3 {
    margin: 0;

    .el-button {
      float: right;
    }
  }

  .billing-title {
    font-size: 14px;
    margin: 0 0 10px;
  }

  .payment-info {
    position: relative;
    height: 70px;
    line-height: 17px;
    padding-left: 60px;
    padding-top: 4px;
    background-repeat: no-repeat;
    background-position: top -5px left;
    background-size: 50px;

    small {
      color: $--color-text-gray;
      font-size: 10px;
    }

    &.visa {
      background-image: url(../../assets/card-visa.svg);
    }
    &.amex {
      background-image: url(../../assets/card-amex.svg);
    }
    &.mastercard {
      background-image: url(../../assets/card-mastercard.svg);
    }

    .el-button, .el-tag {
      position: absolute;
      top: 1px;
      right: 0;

      &.el-button {
        padding: 3px 5px;

        &.el-button + .el-button {
          right: 30px;
        }
      }

      &.el-button--text {
        color: $--button-info-background-color;
      }
    }
  }

  .bank-info {
    height: 50px;
    line-height: 17px;

    small {
      color: $--color-text-gray;
    }
    svg {
      height: 37px;
      float: left;
      position: relative;
      margin-right: 5px;
      top: -2px;
    }
  }
</style>
